import {MarkdownRenderer, type MarkdownRendererProps} from '@github-ui/copilot-markdown'
import React, {useCallback, useMemo, useRef} from 'react'

import {ContentPreviewBlockContext} from '../markdown-extensions/ContentPreviewBlockContext'
import fileViewExtension from '../markdown-extensions/file-blocks/file-blocks'
import draftIssueBlockExtension from '../markdown-extensions/issue-blocks/draft-issue/extension'
import draftIssueTreeExtension from '../markdown-extensions/issue-blocks/draft-issue-tree/extension'
import issueLinkBlockExtension from '../markdown-extensions/issue-blocks/issue-link/extension'
import issueListBlockExtension from '../markdown-extensions/issue-blocks/issue-list/extension'
import issueTreeExtension from '../markdown-extensions/issue-blocks/issue-tree/extension'
import styles from './MarkdownViewer.module.css'

interface MarkdownViewerProps
  extends
    Omit<MarkdownRendererProps, 'onOpenFile'>,
    Omit<ContentPreviewBlockContext, 'hasAutoOpenedPreviewPaneRef' | 'setHasAutoOpenedPreviewPane'> {
  onGitHubLinkClick?: (event: MouseEvent | React.MouseEvent<HTMLElement>) => void
}

export const MarkdownViewer = ({
  messageId,
  messageIndex,
  messageTimestamp,
  autoOpenPreviewPane,
  markdown,
  references,
  onGitHubLinkClick,
  ...rendererProps
}: MarkdownViewerProps) => {
  const hasAutoOpenedPreviewPaneRef = useRef(false)
  const setHasAutoOpenedPreviewPane = useCallback((value: boolean) => {
    hasAutoOpenedPreviewPaneRef.current = value
  }, [])
  const extensions = useMemo(() => {
    const fileBlocksInMessage: string[] = []
    return [
      ...(rendererProps.extensions ?? []),
      fileViewExtension(fileBlocksInMessage),
      issueLinkBlockExtension(),
      issueListBlockExtension(),
      issueTreeExtension(),
      draftIssueBlockExtension(),
      draftIssueTreeExtension(),
    ]
  }, [rendererProps.extensions])

  return (
    <ContentPreviewBlockContext
      value={useMemo(
        () => ({
          messageId,
          messageIndex,
          messageTimestamp,
          autoOpenPreviewPane,
          markdown,
          references,
          hasAutoOpenedPreviewPaneRef,
          setHasAutoOpenedPreviewPane,
        }),
        [
          messageId,
          messageIndex,
          messageTimestamp,
          autoOpenPreviewPane,
          markdown,
          references,
          hasAutoOpenedPreviewPaneRef,
          setHasAutoOpenedPreviewPane,
        ],
      )}
    >
      <MarkdownRenderer
        {...rendererProps}
        markdown={markdown}
        extensions={extensions}
        chatMode="immersive"
        className={styles.markdownOverrides}
        onLinkClick={onGitHubLinkClick}
      />
    </ContentPreviewBlockContext>
  )
}

export const MemoizedMarkdownViewer = React.memo(MarkdownViewer)
